import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ADR_REVIEW } from "../../../_constants/actionConstants";
import { adrSections } from "../../../_constants/docSections";
import { AdrCorrectionCommentsV3 } from "./AdrCorrectionCommentsV3";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback } from "react";

export function AdrReviewHeaderV3(props) {
  const adr = useSelector((state) => state.adr.openAdrs[props.tabId] || null);
  const user = useSelector((state) => state.authentication.user || null);

  const dispatch = useDispatch();

  const addBlank = (fieldInfo) => {
    if (user)
      dispatch({
        type: ADR_REVIEW.CORRECTION_COMMENTS.ADD_BLANK,
        tabId: props.tabId,
        byUserId: user.userId,
        section: adrSections.ADR_SECTION_HEADER,
        fieldInfo,
      });
  };

  /**
   * Add correction comment when a reviewable field is clicked/tapped.
   * @param {Object[]} field - type as defined below
   */
  const handleClick_field = (field) => (e) => {
    const { reviewerInitials, userIsReviewer } = props;
    const { name, label } = field;
    e.preventDefault();
    // All header field names map to the provided fieldInfo's name, EXCEPT
    // 'shift'. For headers, values do not need to be labeled above correction
    // comments since they are already displayed in the body of header - so
    // the value property is populated here for consistency of use only.
    // Also, only add correction comment if one does not exist for the provided
    // field already.
    if (!hasCorrectionComment(name) && adr.isInReview && userIsReviewer)
      addBlank({
        [name]: 0,
        name,
        value: name in adr ? adr[name] : 0,
        label,
        reviewerInitials,
      });
  };

  const hasCorrectionComment = useCallback(
    (name) =>
      (adr.correctionComments || [])
        .filter((cc) => cc.section === adrSections.ADR_SECTION_HEADER && cc.fieldInfo)
        .some((cc) => name in cc.fieldInfo),
    [adr.correctionComments]
  );

  const renderShift = () => {
    return (
      <table>
        <thead>
          <tr className="border-bottom">
            <th></th>
            <th className="px-2">
              <em>Begin</em>
            </th>
            <th className="px-2">
              <em>End</em>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-bottom">
            <td>Contractor:</td>
            <td className="px-2 review-hover">{adr.contractorStartShift || ""}</td>
            <td className="px-2 review-hover">{adr.contractorEndShift || ""}</td>
          </tr>
          <tr className="border-bottom">
            <td>Engineer:</td>
            <td className="px-2 review-hover">{adr.engineerStartShift || ""}</td>
            <td className="px-2 review-hover">{adr.engineerEndShift || ""}</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderWeather = () => {
    const weatherCondition = adr
      ? ["Clear", "Overcast", "Foggy", "Rain", "Snow"][+adr.weatherCondition] || ""
      : "";
    return (
      <React.Fragment>
        {weatherCondition}, T<sub>low</sub>: {adr ? adr.lowTemp || "-" : "-"} deg, T
        <sub>high</sub>: {adr ? adr.highTemp || "-" : "-"} deg
      </React.Fragment>
    );
  };

  /** @type { {label: string, name: string, renderData: function}[] } */
  const headerFields = [
    {
      label: "Written By",
      name: "authorFullName",
      renderData: () =>
        adr ? (
          <span>{`${adr.authorFullName} (${adr.authorEmployeeNo})`}</span>
        ) : (
          <span>(N/A)</span>
        ),
    },
    {
      label: "Report Date",
      name: "reportStartDate",
      renderData: () =>
        adr ? (
          <span>{moment(adr.reportStartDate).format("MM/DD/YYYY (ddd)")}</span>
        ) : (
          <span>(N/A)</span>
        ),
    },
    {
      label: "Report No.",
      name: "reportNo",
      renderData: () =>
        adr ? (
          <span>{adr.reportNo + (adr.reportNoSuffix || "")}</span>
        ) : (
          <span>(N/A)</span>
        ),
    },
    {
      label: "Subcategory",
      name: "customCategory",
      renderData: () => (adr ? <span>{adr.customCategory}</span> : <span>(N/A)</span>),
    },
    { label: "Shift", name: "shift", renderData: () => renderShift() },
    {
      label: "Engineer OT",
      name: "engineerOT",
      renderData: () => (adr ? <span>{adr.engineerOT}</span> : <span>(N/A)</span>),
    },
    {
      label: "Engineer OT Reason",
      name: "engineerOTReason",
      renderData: () =>
        adr ? <span>{adr.engineerOTReason || "(N/A)"}</span> : <span>(N/A)</span>,
    },
    {
      label: "Engineer On Leave",
      name: "engineerOnLeave",
      renderData: () =>
        adr && (
          <FontAwesomeIcon
            icon={adr.engineerOnLeave ? "check-square" : ["far", "square"]}
            className="fa-fw"
          />
        ),
    },
    {
      label: "Weather",
      name: "weatherCondition",
      renderData: () => renderWeather(),
    },
    {
      label: "Weather Comments",
      name: "weatherComment",
      renderData: () => (adr ? <span>{adr.weatherComment}</span> : <span>(N/A)</span>),
    },
    {
      label: "Non-Working Day",
      name: "nonWorkingDay",
      renderData: () =>
        adr && (
          <>
            <FontAwesomeIcon
              icon={adr.nonWorkingDay ? "check-square" : ["far", "square"]}
              className="fa-fw mr-2"
            />
            <small>(Determined by RE)</small>
          </>
        ),
    },
  ];

  const corrComms = useMemo(
    () =>
      adr
        ? (adr.correctionComments || []).filter(
            (cc) => cc.section === adrSections.ADR_SECTION_HEADER
          )
        : [],
    [adr]
  );

  return (
    <div className="row mb-0">
      <div className="col-lg-3 col-md-4 pr-2">{props.jobStamp}</div>
      <div className="col-lg-6 col-md-8 pr-1">
        <div>
          <hr className="my-0" />
          {headerFields.map((field, index) => (
            <React.Fragment key={`adrReviewHeaderRow_${index}`}>
              <div
                className={`d-flex flex-row ${
                  props.userIsReviewer && adr.isInReview ? "reviewable-item" : ""
                } ${hasCorrectionComment(field.name) ? "has-comment" : ""}`}
                onClick={handleClick_field(field)}
              >
                <div className="review-header-label">
                  <span>
                    <b>{field.label}:</b>
                  </span>
                </div>
                <div className="review-header-data" id={index}>
                  {field.renderData()}
                </div>
              </div>
              <hr className="my-0" />
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="col-lg-3 col-md-12 comment-column border-left">
        <AdrCorrectionCommentsV3
          section={adrSections.ADR_SECTION_HEADER}
          correctionComments={corrComms}
          {...props}
        />
      </div>
    </div>
  );
}
